import type { McpServerAuthType, McpServerCreateRequest } from "../../types";

type GatewayFormWatchValues = Pick<
  McpServerCreateRequest,
  "name" | "auth_type" | "user_source_id" | "instructions"
> & {
  tool_filter?: { allowed_tools?: string[] };
};

type GatewayFormStepState = {
  nameComplete: boolean;
  toolsComplete: boolean;
  audienceComplete: boolean;
  instructionsComplete: boolean;
};

export type GatewayFormState = {
  stepState: GatewayFormStepState;
  missingRequirements: string[];
  readyToCreate: boolean;
  selectedToolCount: number;
  authType: McpServerAuthType;
};

export function getGatewayFormState(
  values: GatewayFormWatchValues,
  { isEditing }: { isEditing: boolean }
): GatewayFormState {
  const allowedTools = values.tool_filter?.allowed_tools;
  const authType = values.auth_type ?? "arcade";
  const userSourceId = values.user_source_id;

  const nameComplete = Boolean(values.name?.trim());
  const toolsComplete = isEditing || (allowedTools?.length ?? 0) > 0;
  const audienceComplete = authType !== "user_source" || Boolean(userSourceId);
  const instructionsComplete = Boolean(values.instructions?.trim());
  const selectedToolCount = allowedTools?.length ?? 0;

  const missingRequirements = [
    nameComplete ? null : "add a name",
    toolsComplete ? null : "select at least one tool",
    authType === "user_source" && !userSourceId
      ? "select a user source for external users"
      : null,
  ].filter((requirement): requirement is string => requirement !== null);

  return {
    stepState: {
      nameComplete,
      toolsComplete,
      audienceComplete,
      instructionsComplete,
    },
    missingRequirements,
    readyToCreate: missingRequirements.length === 0,
    selectedToolCount,
    authType,
  };
}

const requirementListFormat = new Intl.ListFormat("en", {
  type: "conjunction",
});

const formatRequirementList = (requirements: string[]): string =>
  requirementListFormat.format(requirements);

/** The one-line "Required: …" string shared by the footer and the blocked-submit toast. */
export const formatMissingRequirements = (requirements: string[]): string =>
  `Required: ${formatRequirementList(requirements)}.`;

/**
 * Gate submit beyond Zod — tools are required on create but not in the schema.
 * Returns a toast message when a blocked submit should give feedback, otherwise
 * null (i.e. the form is ready and submit may proceed). Since a not-ready form
 * always has at least one missing requirement, a null return is exactly
 * `readyToCreate`.
 */
export function getSubmitBlockToast(
  values: McpServerCreateRequest,
  { isEditing }: { isEditing: boolean }
): string | null {
  const { missingRequirements, readyToCreate } = getGatewayFormState(values, {
    isEditing,
  });

  if (readyToCreate) {
    return null;
  }

  return formatMissingRequirements(missingRequirements);
}
